'use client';

import { useDeepEqualMemo } from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  defaultDeviceType,
  DeviceTypeProps,
  ScreenSize
} from '~/constants/common/defaultDeviceType';
import { DeviceSource } from '~/constants/common/DeviceSource';
import type { DeviceType, NativeDeviceType } from '~/contracts/common';
import type { DeepPartial } from '~/types';

export type ContextType = DeviceTypeProps & {
  screenSize: ScreenSize;
  setDeviceType: React.Dispatch<React.SetStateAction<DeviceType>>;
  setScreenSize: React.Dispatch<React.SetStateAction<ScreenSize>>;
};

export const defaultContext: ContextType = {
  ...defaultDeviceType,
  screenSize: {
    height: 0,
    width: 0
  },
  setDeviceType() {},
  setScreenSize() {}
};

const DeviceContext = createContext<ContextType>(defaultContext);

export type OwnProps = {
  browserName: string;
  deviceName: string;
  deviceType: DeviceType;
  isBot: boolean;
  nativeDeviceType: NativeDeviceType;
};

export function DeviceContextProvider(
  props: React.PropsWithChildren<OwnProps>
) {
  const [deviceType, setDeviceType] = useState(props.deviceType);
  const [nativeDeviceType] = useState(props.nativeDeviceType);
  const [screenSize, setScreenSize] = useState(defaultContext.screenSize);

  const value = useMemo<ContextType>(
    () => ({
      browserName: props.browserName,
      deviceName: props.deviceName,
      deviceType,
      isBot: props.isBot,
      nativeDeviceType,
      screenSize,
      setDeviceType,
      setScreenSize
    }),
    [
      deviceType,
      nativeDeviceType,
      props.browserName,
      props.deviceName,
      props.isBot,
      screenSize
    ]
  );

  return (
    <DeviceContext.Provider value={value}>
      {props.children}
    </DeviceContext.Provider>
  );
}

DeviceContextProvider.displayName = 'DeviceContextProvider';

type ConditionalDeviceContextProviderProps = React.PropsWithChildren<{
  deviceContext?: OwnProps;
}>;

// To support using SearchProviders in both routers, we conditionally render
// DeviceContextProvider based on the presence of deviceContext
// props which is still provided by pages router getProps
export function ConditionalDeviceContextProvider({
  children,
  deviceContext
}: ConditionalDeviceContextProviderProps) {
  return deviceContext ? (
    <DeviceContextProvider {...deviceContext}>{children}</DeviceContextProvider>
  ) : (
    <>{children}</>
  );
}

export const generateDeviceContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export const useDeviceType = (): DeviceType =>
  useDeepEqualMemo(useContext(DeviceContext).deviceType);

export const useIsMobile = (): boolean => useDeviceType().isMobile;

export const useIsTablet = (): boolean => useDeviceType().isTablet;

export const useIsDesktop = (): boolean => useDeviceType().isDesktop;

export const useIsLargeDesktop = (): boolean =>
  useDeviceType().isLargeDesktop ?? false;

export const useIsMobileWithOrientation = (): boolean =>
  useDeviceType().isMobileWithOrientation;

export const useNativeDeviceType = (): NativeDeviceType =>
  useDeepEqualMemo(useContext(DeviceContext).nativeDeviceType);

export const useIsNative = (): boolean => useNativeDeviceType().isNative;

export const useIsNativeIOS = (): boolean => {
  const { isNative, platform } = useNativeDeviceType();

  return isNative && platform === 'ios';
};

export const useScreenSize = (): ScreenSize =>
  useDeepEqualMemo(useContext(DeviceContext).screenSize);

export function useIsSafari() {
  const browser = useContext(DeviceContext).browserName.toLowerCase();

  return browser.includes('safari') || browser.includes('ios');
}

export function useHasAccurateDeviceType() {
  return useDeviceType().source === DeviceSource.Browser;
}

export function MockedDeviceContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <DeviceContext.Provider value={generateDeviceContext(mockValue)}>
      {children}
    </DeviceContext.Provider>
  );
}

export default DeviceContext;
